<template>
  <div
    class="activity-chart"
    @mouseleave="emit('hoverEnd')"
    @touchstart.passive="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="clearAfterChart"
  >
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  Chart,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type ChartConfiguration,
  type Plugin,
  type TooltipItem,
} from 'chart.js';
import { withAlpha } from '../../../utils/recordingActivity/colorScale';
import { withUnit } from '../../../utils/recordingActivity/format';
import { isTouchDrag, type TouchPoint } from '../../../utils/recordingActivity/touchDrag';
import {
  profileRange,
  type ActivitySeries,
  type ActivityTooltip,
  type BackdropHighPoint,
  type ChartPoint,
} from '../../../utils/recordingActivity/recordingActivity';

Chart.register(LineController, LineElement, PointElement, LinearScale, Filler, Tooltip);

const props = defineProps<{
  series: ActivitySeries;
  xUnit: string;
  /** In the backdrop's unit, so a flat walk's profile stays low. */
  backdropMinimumSpread: number;
  tooltip: (point: ChartPoint) => ActivityTooltip;
  highPoint?: BackdropHighPoint | null;
}>();

const emit = defineEmits<{
  /** The hovered reading's distance along the track, in the chart's x unit. */
  (e: 'hover', x: number): void;
  (e: 'hoverEnd'): void;
}>();

const canvas = ref<HTMLCanvasElement | null>(null);
let chart: Chart<'line', ChartPoint[]> | null = null;

const TICK_COLOR = 'rgba(255, 255, 255, 0.6)';
const GRID_COLOR = 'rgba(255, 255, 255, 0.1)';
const BACKDROP_COLOR = 'rgba(255, 255, 255, 0.07)';
const BASELINE_COLOR = 'rgba(255, 255, 255, 0.3)';
const HIGH_POINT_LINE = 'rgba(255, 255, 255, 0.3)';
const HIGH_POINT_MUTED = 'rgba(255, 255, 255, 0.6)';
const HIGH_POINT_PILL = 'rgba(23, 24, 29, 0.92)';
const HIGH_POINT_PILL_BORDER = 'rgba(255, 255, 255, 0.18)';
const HIGH_POINT_RING_FILL = '#17181D';
const HIGH_POINT_RING_RADIUS = 5;
const PILL_HEIGHT = 24;
// Wide enough for "15:00 /km"; the same on every tab, so the plot and the elevation behind it don't shift sideways.
const Y_AXIS_WIDTH = 64;
// The strip above the plot the pill sits in, so the reading's line can never run under it.
const PILL_BAND = PILL_HEIGHT + 8;

function pillTop(target: Chart) {
  return target.chartArea.top - PILL_BAND + (PILL_BAND - PILL_HEIGHT) / 2;
}

// Keyed to the y scale, so each reading takes its value's colour wherever it sits on the line; or,
// with stops, to the x scale, so each stretch takes its own.
function paint(target: Chart, colors: ActivitySeries['colors'], lowAlpha: number, highAlpha = lowAlpha): CanvasGradient | string {
  if ('color' in colors) return withAlpha(colors.color, (lowAlpha + highAlpha) / 2);
  const { chartArea, scales } = target;
  if ('stops' in colors) {
    if (!chartArea || !scales.x) return withAlpha(colors.stops[0]?.color ?? '#FFFFFF', highAlpha);
    const gradient = target.ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    colors.stops.forEach(({ x, color }) => {
      const offset = (scales.x.getPixelForValue(x) - chartArea.left) / (chartArea.right - chartArea.left);
      gradient.addColorStop(Math.min(Math.max(offset, 0), 1), withAlpha(color, highAlpha));
    });
    return gradient;
  }
  const y = scales.y;
  if (!chartArea || !y) return withAlpha(colors.lowColor, lowAlpha);
  const gradient = target.ctx.createLinearGradient(0, y.getPixelForValue(colors.low), 0, y.getPixelForValue(colors.high));
  gradient.addColorStop(0, withAlpha(colors.lowColor, lowAlpha));
  gradient.addColorStop(1, withAlpha(colors.highColor, highAlpha));
  return gradient;
}

// The profile keeps its own height scale, the one the elevation tab's axis uses, so it stays put between tabs.
function profileY(target: Chart, points: ChartPoint[], minimumSpread: number) {
  const { chartArea } = target;
  const { min, max } = profileRange(points, minimumSpread);
  return (y: number) => chartArea.bottom - ((y - min) / (max - min)) * chartArea.height;
}

// A ring on the top of the backdrop and, in the strip above the plot, a pill naming the height.
function drawHighPoint(target: Chart, x: number, y: number, highPoint: BackdropHighPoint) {
  const { ctx, chartArea } = target;
  const family = getComputedStyle(target.canvas).fontFamily;
  const bold = `700 12px ${family}`;
  const regular = `400 12px ${family}`;
  const padding = 10;
  const gap = 6;
  const triangle = 8;

  ctx.save();
  ctx.font = bold;
  const labelWidth = ctx.measureText(highPoint.label).width;
  ctx.font = regular;
  const valueWidth = ctx.measureText(highPoint.value).width;
  const width = padding * 2 + triangle + gap + labelWidth + gap + valueWidth;
  const left = Math.max(chartArea.left, Math.min(x - width / 2, chartArea.right - width));
  const top = pillTop(target);
  const middle = top + PILL_HEIGHT / 2;

  ctx.beginPath();
  ctx.roundRect(left, top, width, PILL_HEIGHT, PILL_HEIGHT / 2);
  ctx.fillStyle = HIGH_POINT_PILL;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = HIGH_POINT_PILL_BORDER;
  ctx.stroke();

  let cursor = left + padding;
  ctx.beginPath();
  ctx.moveTo(cursor, middle + 4);
  ctx.lineTo(cursor + triangle / 2, middle - 4);
  ctx.lineTo(cursor + triangle, middle + 4);
  ctx.closePath();
  ctx.fillStyle = HIGH_POINT_MUTED;
  ctx.fill();

  cursor += triangle + gap;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.font = bold;
  ctx.fillStyle = '#fff';
  ctx.fillText(highPoint.label, cursor, middle);
  cursor += labelWidth + gap;
  ctx.font = regular;
  ctx.fillStyle = HIGH_POINT_MUTED;
  ctx.fillText(highPoint.value, cursor, middle);

  ctx.beginPath();
  ctx.arc(x, y, HIGH_POINT_RING_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = HIGH_POINT_RING_FILL;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = HIGH_POINT_MUTED;
  ctx.stroke();
  ctx.restore();
}

// Drawn under the line rather than as a dataset, so hovering only ever finds the reading. The high point is
// marked on every tab, the elevation tab's own line included; only the others draw the profile behind.
function backdropPlugin(series: ActivitySeries, minimumSpread: number, highPoint: BackdropHighPoint | null): Plugin<'line'> {
  const points = series.profile;
  return {
    id: 'activityBackdrop',
    beforeDatasetsDraw(target) {
      if (!points || points.length < 2) return;
      const { ctx, chartArea, scales } = target;
      const toY = profileY(target, points, minimumSpread);

      ctx.save();
      if (series.backdrop) {
        ctx.beginPath();
        ctx.moveTo(scales.x.getPixelForValue(points[0].x), chartArea.bottom);
        points.forEach((p) => ctx.lineTo(scales.x.getPixelForValue(p.x), toY(p.y)));
        ctx.lineTo(scales.x.getPixelForValue(points[points.length - 1].x), chartArea.bottom);
        ctx.closePath();
        ctx.fillStyle = BACKDROP_COLOR;
        ctx.fill();
      }

      if (highPoint) {
        const x = scales.x.getPixelForValue(highPoint.x);
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.moveTo(x, pillTop(target) + PILL_HEIGHT);
        ctx.lineTo(x, chartArea.bottom);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = HIGH_POINT_LINE;
        ctx.stroke();
      }
      ctx.restore();
    },
    afterDatasetsDraw(target) {
      if (!points || points.length < 2 || !highPoint) return;
      const toY = profileY(target, points, minimumSpread);
      drawHighPoint(target, target.scales.x.getPixelForValue(highPoint.x), toY(highPoint.y), highPoint);
    },
  };
}

const hoverLinePlugin: Plugin<'line'> = {
  id: 'activityHoverLine',
  afterDatasetsDraw(target) {
    const active = target.tooltip?.getActiveElements?.() ?? [];
    if (active.length === 0) return;
    const { ctx, chartArea } = target;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(active[0].element.x, chartArea.top);
    ctx.lineTo(active[0].element.x, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.stroke();
    ctx.restore();
  },
};

function config(series: ActivitySeries): ChartConfiguration<'line', ChartPoint[]> {
  return {
    type: 'line',
    data: {
      datasets: [
        {
          data: series.points,
          parsing: false,
          borderWidth: 2,
          borderColor: (ctx) => paint(ctx.chart, series.colors, 1),
          backgroundColor: (ctx) => paint(ctx.chart, series.colors, 0.08, 0.3),
          fill: series.baseline ? 'origin' : 'start',
          tension: 0.35,
          cubicInterpolationMode: 'monotone',
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderWidth: 0,
        },
      ],
    },
    plugins: [backdropPlugin(series, props.backdropMinimumSpread, props.highPoint ?? null), hoverLinePlugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { top: props.highPoint ? PILL_BAND : 0 } },
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      onHover: (_event, elements) => {
        const point = elements[0] && series.points[elements[0].index];
        if (point) emit('hover', point.x);
        else emit('hoverEnd');
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(40, 42, 50, 0.95)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8,
          displayColors: false,
          callbacks: {
            title: (items: TooltipItem<'line'>[]) => props.tooltip(items[0].raw as ChartPoint).title,
            label: (item: TooltipItem<'line'>) => props.tooltip(item.raw as ChartPoint).lines,
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: 0,
          max: series.xMax,
          afterBuildTicks: (scale) => {
            scale.ticks = series.xTicks.map((value) => ({ value }));
          },
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: TICK_COLOR,
            font: { size: 11 },
            callback: (value, index, ticks) => {
              const label = series.formatX(Number(value));
              return index === ticks.length - 1 ? `${label} ${props.xUnit}` : label;
            },
          },
        },
        y: {
          type: 'linear',
          reverse: series.reverse,
          min: series.yTicks ? series.yMin : undefined,
          max: series.yTicks ? series.yMax : undefined,
          suggestedMin: series.yMin,
          suggestedMax: series.yMax,
          afterBuildTicks: (scale) => {
            if (series.yTicks) scale.ticks = series.yTicks.map((value) => ({ value }));
          },
          afterFit: (scale) => {
            scale.width = Y_AXIS_WIDTH;
          },
          grid: { color: (ctx) => (series.baseline && ctx.tick?.value === 0 ? BASELINE_COLOR : GRID_COLOR) },
          border: { display: false },
          ticks: {
            color: TICK_COLOR,
            font: { size: 11 },
            maxTicksLimit: 4,
            autoSkip: !series.yTicks,
            callback: (value, _index, ticks) => {
              const values = ticks.map((tick) => tick.value);
              const top = series.reverse ? Math.min(...values) : Math.max(...values);
              const label = series.formatValue(Number(value));
              return value === top ? withUnit(label, series.unit) : label;
            },
          },
        },
      },
    },
  };
}

function render() {
  chart?.destroy();
  chart = null;
  if (!canvas.value || props.series.points.length < 2) return;
  chart = new Chart(canvas.value, config(props.series));
}

let touchStart: TouchPoint | null = null;
let isDragging = false;

function firstTouch(event: TouchEvent): TouchPoint | null {
  const touch = event.touches[0];
  return touch ? { x: touch.clientX, y: touch.clientY } : null;
}

function onTouchStart(event: TouchEvent) {
  touchStart = firstTouch(event);
  isDragging = false;
}

function onTouchMove(event: TouchEvent) {
  const point = firstTouch(event);
  if (touchStart && point && isTouchDrag(touchStart, point)) isDragging = true;
}

// Chart.js never hears touchend and handles each touchmove a frame late, so a drag's reading is cleared a frame after the lift; a tap keeps it.
function onTouchEnd() {
  if (isDragging) clearAfterChart();
}

function clearAfterChart() {
  requestAnimationFrame(clearReading);
}

function clearReading() {
  if (chart) {
    chart.setActiveElements([]);
    chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
    chart.update();
  }
  emit('hoverEnd');
}

onMounted(render);
watch(() => props.series, render);
onBeforeUnmount(() => {
  chart?.destroy();
  chart = null;
});
</script>

<style scoped>
.activity-chart {
  position: relative;
  height: 240px;
}

@media (max-width: 575.98px) {
  .activity-chart {
    height: 200px;
  }
}
</style>
